import React from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import {
	casesLawyerService,
	GET_CASES,
	GET_LAWYER_CASES_LIST,
	GET_PINNED_CASES,
} from '@/api';
import { isTerminalCaseStatus } from '@/const/cases';
import AppRoutes from '@/const/routes';
import useModal from '@/hooks/useModal';
import { useNotification } from '@/hooks/useNotification';
import { CaseStatusFilter, ErrorHttpResponse } from '@/interfaces';

interface UseEndOrDeleteCaseParams {
	caseId?: string;
	caseTitle: string;
	clientCount: number;
	progressName?: CaseStatusFilter | string;
	redirectOnSuccess?: boolean;
}

export const useEndOrDeleteCase = ({
	caseId,
	caseTitle,
	clientCount,
	progressName,
	redirectOnSuccess = false,
}: UseEndOrDeleteCaseParams) => {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { toastSuccess, toastError } = useNotification();
	const { register, setValue } = useForm();
	const { Modal, show, hide, isOpen } = useModal();
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const isSubmittingRef = React.useRef(false);

	const hasClients = clientCount > 0;
	const isEnded = isTerminalCaseStatus(progressName as CaseStatusFilter);
	const isDisabled = !caseId || isSubmitting || isEnded;

	const open = () => {
		if (isDisabled) return;
		setValue('caseName', '');
		show();
	};

	const close = () => {
		if (isSubmittingRef.current) return;
		setValue('caseName', '');
		hide();
	};

	const confirm = async (typedName: string): Promise<void> => {
		if (!caseId || isSubmittingRef.current) return;
		if (!caseTitle || typedName !== caseTitle) return;
		isSubmittingRef.current = true;
		setIsSubmitting(true);
		try {
			if (hasClients) {
				await casesLawyerService.abort(caseId);
				toastSuccess('This case has been successfully ended.');
			} else {
				await casesLawyerService.delete(caseId);
				toastSuccess('The case has been deleted successfully.');
			}
			queryClient.refetchQueries({ queryKey: [GET_LAWYER_CASES_LIST] });
			queryClient.refetchQueries({ queryKey: [GET_PINNED_CASES] });
			queryClient.refetchQueries({ queryKey: [GET_CASES] });
			hide();
			if (redirectOnSuccess) {
				router.push(AppRoutes.LAWYERAPP_CASES);
			}
		} catch (error: unknown) {
			const httpError = error as Partial<ErrorHttpResponse>;
			const httpStatus = httpError.status ?? httpError.statusCode;
			if (!hasClients && httpStatus === 400) {
				toastError(
					'The case is currently in progress and cannot be deleted at this time.'
				);
			} else if (hasClients) {
				toastError(
					'An issue occurred while ending the case. Please try again.'
				);
			} else {
				toastError(
					'An issue occurred while deleting the case. Please try again.'
				);
			}
		} finally {
			isSubmittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	return {
		hasClients,
		isEnded,
		isDisabled,
		isSubmitting,
		isOpen,
		Modal,
		register,
		open,
		close,
		confirm,
		caseTitle,
	};
};
